import { HttpClient, HttpErrorResponse, HttpResponse } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { ErrorService } from '@cosmos/angular/utils';
import { Concrete, MaturityCalibrationResult } from '@cosmos/shared';
import { catchError, map, Observable } from 'rxjs';
import { environment } from '../../../../../../environments/environment';

@Injectable({
  providedIn: 'root'
})
export class ConcreteModelsService {

  constructor(
    private http: HttpClient,
    private errorService: ErrorService,
  ) { }

  find(query=''): Observable<HttpResponse<Concrete[]>> {
    return this.http
      .get<Concrete[]>(`${environment.API_URL}/concrete-models${query}`, {observe: 'response'})
        .pipe(
          map(response => {return response;}),
          catchError( (error) =>{this.errorService.handleError(error); throw error;})
        );
  }

  findById(id: string): Observable<HttpResponse<Concrete[]>> {
    return this.http
      .get<Concrete[]>(`${environment.API_URL}/concrete-models/${id}`, {observe: 'response'})
        .pipe(
          map(response => {return response;}),
          catchError( (error) =>{this.errorService.handleError(error); throw error;})
        );
  }

  create(body: Concrete) {
    return this.http
      .post(`${environment.API_URL}/concrete-models`, body, {observe: 'response'})
        .pipe(
          map(response => {return response;}),
          catchError( (error) =>{this.errorService.handleError(error); throw error;})
        );
  }

  update(id: string, body: any) {
    return this.http
      .patch(`${environment.API_URL}/concrete-models/${id}`, body, {observe: 'response'})
        .pipe(
          map(response => {return response;}),
          catchError( (error) =>{this.errorService.handleError(error); throw error;})
        );
  }

  setActive(id: string, isActive: boolean) {
    return this.http
      .patch(`${environment.API_URL}/concrete-models/${id}/active`, { isActive }, {observe: 'response'})
        .pipe(
          map(response => {return response;}),
          catchError( (error) =>{this.errorService.handleError(error); throw error;})
        );
  }

  setCalibrationTest(id: string, sampleDate: string | null, result: MaturityCalibrationResult) {
    return this.http
      .patch(`${environment.API_URL}/concrete-models/${id}/calibration-test`, { sampleDate, result }, {observe: 'response'})
        .pipe(
          map(response => {return response;}),
          catchError( (error) =>{this.errorService.handleError(error); throw error;})
        );
  }

  addCalibrationResult(id: string, result: MaturityCalibrationResult) {
    return this.http
      .patch(`${environment.API_URL}/concrete-models/${id}/calibration-test/results`, { result }, {observe: 'response'})
        .pipe(
          map(response => {return response;}),
          catchError( (error) =>{this.errorService.handleError(error); throw error;})
        );
  }

  setFitStats(id: string, fitStats: { r2: number; method: string; notes?: string }) {
    return this.http
      .patch(`${environment.API_URL}/concrete-models/${id}/fit-stats`, fitStats, {observe: 'response'})
        .pipe(
          map(response => {return response;}),
          catchError( (error) =>{this.errorService.handleError(error); throw error;})
        );
  }

  /** Uploads a calibration test document and appends it onto calibrationTest.files[] in one request — see backend for why this isn't split into upload-then-link (orphan-file risk). */
  uploadCalibrationFile(id: string, file: File) {
    const formData = new FormData();
    formData.append('file', file);
    formData.append('filename', file.name);
    return this.http
      .post(`${environment.API_URL}/concrete-models/${id}/files`, formData, {observe: 'response'})
        .pipe(
          map(response => {return response;}),
          catchError( (error) =>{this.errorService.handleError(error); throw error;})
        );
  }

  serveCalibrationFileBlob(id: string, fileId: string) {
    return this.http
      .get(`${environment.API_URL}/concrete-models/${id}/files/${fileId}/serve`, { responseType: 'blob' })
        .pipe(
          map((blob) => ({ url: URL.createObjectURL(blob), contentType: blob.type })),
          catchError( (error) =>{this.errorService.handleError(error); throw error;})
        );
  }

  searchMeasurements(query: string) {
    return this.http
      .get<any[]>(`${environment.API_URL}/concrete-models/measurements/search?q=${encodeURIComponent(query)}`, {observe: 'response'})
        .pipe(
          map(response => {return response;}),
          catchError( (error) =>{this.errorService.handleError(error); throw error;})
        );
  }

  delete(id: string) {
    return this.http
      .delete(`${environment.API_URL}/concrete-models/${id}`, {observe: 'response'})
        .pipe(
          map(response => {return response;}),
          catchError( (error) =>{this.errorService.handleError(error); throw error;})
        );
  }

  findCemex(region=''): Observable<HttpResponse<Concrete[]>> {
    return this.http
      .get<Concrete[]>(`${environment.API_URL}/concretes/cemex?plant=${region}`, {observe: 'response'})
        .pipe(
          map(response => {return response;}),
          catchError( (error) =>{this.errorService.handleError(error); throw error;})
        );
  }

}
